import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ProfileLayout } from '@/layouts/ProfileLayout';
import { AdminLayout } from '@/layouts/AdminLayout';

// Direct imports for main routes (no lazy loading = no flash)
import BoardsPage from '@/pages/boards/BoardsPage';
import BoardDetailPage from '@/pages/boards/BoardDetailPage';
import PostDetailPage from '@/pages/posts/PostDetailPage';

// Lazy load less frequently used pages
const VotersPage = lazy(() => import('@/pages/posts/VotersPage'));
const SearchPage = lazy(() => import('@/pages/SearchPage'));
const VerifyEmailPage = lazy(() => import('@/pages/VerifyEmailPage'));

// Auth pages (lazy - only used occasionally)
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const MigrationPromptPage = lazy(() => import('@/pages/auth/MigrationPromptPage'));

// Profile pages (lazy - behind auth)
const ProfileSettingsPage = lazy(() => import('@/pages/profile/ProfileSettingsPage'));
const ProfilePostsPage = lazy(() => import('@/pages/profile/ProfilePostsPage'));
const ProfileVotesPage = lazy(() => import('@/pages/profile/ProfileVotesPage'));
const ProfileActivityPage = lazy(() => import('@/pages/profile/ProfileActivityPage'));
const ProfileSecurityPage = lazy(() => import('@/pages/profile/ProfileSecurityPage'));

// Admin pages (lazy - rarely used)
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminBoardsPage = lazy(() => import('@/pages/admin/AdminBoardsPage'));
const AdminPostsPage = lazy(() => import('@/pages/admin/AdminPostsPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage'));
const AdminAuditLogsPage = lazy(() => import('@/pages/admin/AdminAuditLogsPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/AdminAnalyticsPage'));
const AdminDuplicatesPage = lazy(() => import('@/pages/admin/AdminDuplicatesPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/AdminIntegrationsPage'));

// Export empty preloadRoutes for backwards compatibility
export const preloadRoutes = {
  boards: () => Promise.resolve(),
  boardDetail: () => Promise.resolve(),
  postDetail: () => Promise.resolve(),
  voters: () => import('@/pages/posts/VotersPage'),
  search: () => import('@/pages/SearchPage'),
  login: () => import('@/pages/auth/LoginPage'),
  register: () => import('@/pages/auth/RegisterPage'),
  profile: () => Promise.all([
    import('@/pages/profile/ProfileSettingsPage'),
    import('@/pages/profile/ProfilePostsPage'),
    import('@/pages/profile/ProfileVotesPage'),
    import('@/pages/profile/ProfileActivityPage'),
    import('@/pages/profile/ProfileSecurityPage'),
  ]),
  admin: () => Promise.all([
    import('@/pages/admin/AdminDashboardPage'),
    import('@/pages/admin/AdminBoardsPage'),
    import('@/pages/admin/AdminPostsPage'),
    import('@/pages/admin/AdminUsersPage'),
    import('@/pages/admin/AdminAuditLogsPage'),
    import('@/pages/admin/AdminAnalyticsPage'),
    import('@/pages/admin/AdminDuplicatesPage'),
    import('@/pages/admin/AdminSettingsPage'),
  ]),
};

// Minimal loading fallback for lazy-loaded routes
function PageLoading() {
  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), 200);
    return () => clearTimeout(timer);
  }, []);

  if (!showSpinner) return null;

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
}

// Wrapper for lazy-loaded components
function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoading />}>{children}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/boards" replace />} />

      {/* Main routes - eagerly loaded, no Suspense needed */}
      <Route path="/boards" element={<BoardsPage />} />
      <Route path="/boards/:slug" element={<BoardDetailPage />} />
      <Route path="/posts/:slug" element={<PostDetailPage />} />

      {/* Less common routes - lazy loaded with Suspense */}
      <Route path="/posts/:slug/voters" element={<LazyRoute><VotersPage /></LazyRoute>} />
      <Route path="/search" element={<LazyRoute><SearchPage /></LazyRoute>} />
      <Route path="/verify-email" element={<LazyRoute><VerifyEmailPage /></LazyRoute>} />

      {/* Auth routes - lazy loaded */}
      <Route path="/auth/login" element={<LazyRoute><LoginPage /></LazyRoute>} />
      <Route path="/auth/register" element={<LazyRoute><RegisterPage /></LazyRoute>} />
      <Route path="/auth/forgot-password" element={<LazyRoute><ForgotPasswordPage /></LazyRoute>} />
      <Route path="/auth/reset-password" element={<LazyRoute><ResetPasswordPage /></LazyRoute>} />
      <Route path="/auth/migration-prompt" element={<LazyRoute><MigrationPromptPage /></LazyRoute>} />

      {/* Profile routes - protected, lazy loaded */}
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfileLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/profile/settings" replace />} />
        <Route path="settings" element={<LazyRoute><ProfileSettingsPage /></LazyRoute>} />
        <Route path="posts" element={<LazyRoute><ProfilePostsPage /></LazyRoute>} />
        <Route path="votes" element={<LazyRoute><ProfileVotesPage /></LazyRoute>} />
        <Route path="activity" element={<LazyRoute><ProfileActivityPage /></LazyRoute>} />
        <Route path="security" element={<LazyRoute><ProfileSecurityPage /></LazyRoute>} />
      </Route>

      {/* Admin routes - protected + admin required, lazy loaded */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<LazyRoute><AdminDashboardPage /></LazyRoute>} />
        <Route path="boards" element={<LazyRoute><AdminBoardsPage /></LazyRoute>} />
        <Route path="posts" element={<LazyRoute><AdminPostsPage /></LazyRoute>} />
        <Route path="users" element={<LazyRoute><AdminUsersPage /></LazyRoute>} />
        <Route path="audit-logs" element={<LazyRoute><AdminAuditLogsPage /></LazyRoute>} />
        <Route path="analytics" element={<LazyRoute><AdminAnalyticsPage /></LazyRoute>} />
        <Route path="duplicates" element={<LazyRoute><AdminDuplicatesPage /></LazyRoute>} />
        <Route path="settings" element={<LazyRoute><AdminSettingsPage /></LazyRoute>} />
        <Route path="integrations" element={<LazyRoute><AdminIntegrationsPage /></LazyRoute>} />
      </Route>

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/boards" replace />} />
    </Routes>
  );
}
